const APP_ROLES = new Set(['admin', 'manager', 'agent']);

const ACCOUNT_ROLE_MARKERS = [
  'manage-account',
  'view-profile',
  'view-consent',
  'delete-account',
  'offline_access',
  'uma_authorization',
];

export function isAccountRole(role) {
  if (!role) return true;
  const lower = String(role).trim().toLowerCase();
  return ACCOUNT_ROLE_MARKERS.some((marker) => lower === marker || lower.startsWith(`${marker}-`));
}

export function isAppRole(role) {
  if (!role) return false;
  return APP_ROLES.has(String(role).trim().toLowerCase());
}

export function normalizeUserRole(role) {
  if (!role) return role;
  const lower = String(role).trim().toLowerCase();
  if (lower.startsWith('manage-account')) return 'Admin';
  if (lower === 'admin') return 'Admin';
  if (lower === 'manager') return 'Manager';
  if (lower === 'agent') return 'Agent';
  return role;
}

function pickAppRole(roles = []) {
  const appRole = roles.find((role) => isAppRole(role) && !isAccountRole(role));
  return appRole ? normalizeUserRole(appRole) : null;
}

function flattenClientRoles(clientRoles, clientId) {
  if (!clientRoles || typeof clientRoles !== 'object' || Array.isArray(clientRoles)) {
    return [];
  }

  const preferredKeys = [];
  if (clientId) {
    preferredKeys.push(clientId);
    const appPrefix = String(clientId).split('-')[0];
    Object.keys(clientRoles).forEach((key) => {
      if (key !== 'account' && key.startsWith(`${appPrefix}-`)) {
        preferredKeys.push(key);
      }
    });
  }

  const orderedKeys = [
    ...preferredKeys,
    ...Object.keys(clientRoles).filter((key) => key !== 'account' && !preferredKeys.includes(key)),
  ];

  const roles = [];
  orderedKeys.forEach((key) => {
    const entry = clientRoles[key];
    if (Array.isArray(entry)) {
      roles.push(...entry);
    }
  });

  return roles;
}

/**
 * Resolve the application role (Admin / Manager / Agent) from Keycloak user payload.
 * Ignores account-level roles like manage-account-links that must not gate routing.
 */
export function resolveUserRole(user, clientId) {
  if (!user) return null;

  if (user.clientRoles && typeof user.clientRoles === 'object' && !Array.isArray(user.clientRoles)) {
    const fromClientObject = pickAppRole(flattenClientRoles(user.clientRoles, clientId));
    if (fromClientObject) return fromClientObject;
  }

  const clientRolesList = Array.isArray(user.client_roles) ? user.client_roles : [];
  const fromList = pickAppRole(clientRolesList);
  if (fromList) return fromList;

  const hasAccountAccess = clientRolesList.some((role) => {
    const lower = String(role).toLowerCase();
    return lower === 'manage-account' || lower.startsWith('manage-account');
  });
  if (hasAccountAccess) return 'Admin';

  const fallback = clientRolesList.find((role) => !isAccountRole(role));
  return fallback ? normalizeUserRole(fallback) : null;
}

export function normalizeClientRolesList(rawUser, clientId) {
  if (!rawUser) return [];

  if (rawUser.clientRoles && typeof rawUser.clientRoles === 'object' && !Array.isArray(rawUser.clientRoles)) {
    return flattenClientRoles(rawUser.clientRoles, clientId);
  }

  if (Array.isArray(rawUser.client_roles)) {
    return rawUser.client_roles;
  }

  return [];
}
